import { z } from "zod";

export const neighbourhoodKeySchema = z.string().regex(/^[a-z][a-z0-9_]*$/);
const coordinateSchema = z.number().finite().nonnegative();

export const neighbourhoodLocationKindSchema = z.enum([
  "castle",
  "resource",
  "hunt",
  "threat",
]);

export const neighbourhoodTravelBandSchema = z.object({
  id: z.string().uuid(),
  key: neighbourhoodKeySchema,
  name: z.string().min(1),
  sortOrder: z.number().int().nonnegative(),
  durationSeconds: z.number().int().positive().nullable(),
}).strict();

export const neighbourhoodSectorSchema = z.object({
  id: z.string().uuid(),
  key: neighbourhoodKeySchema,
  name: z.string().min(1),
  sortOrder: z.number().int().nonnegative(),
  fairnessGroup: neighbourhoodKeySchema.nullable(),
  polygon: z.array(z.object({ x: coordinateSchema, y: coordinateSchema }).strict()),
}).strict();

const neighbourhoodLocationBaseSchema = z.object({
  id: z.string().uuid(),
  key: neighbourhoodKeySchema,
  name: z.string().min(1),
  kind: neighbourhoodLocationKindSchema,
  x: coordinateSchema,
  y: coordinateSchema,
  sortOrder: z.number().int().nonnegative(),
  sectorId: z.string().uuid().nullable(),
  castleRingIndex: z.number().int().min(1).max(12).nullable(),
  stableAssignmentRef: z.string().min(1).nullable(),
  spawnWeight: z.number().int().positive().nullable(),
});

function validateLocationShape(location: {
  kind: "castle" | "resource" | "hunt" | "threat";
  castleRingIndex: number | null;
  stableAssignmentRef: string | null;
}, context: z.RefinementCtx) {
  if (location.kind === "castle" && location.castleRingIndex === null)
    context.addIssue({ code: "custom", path: ["castleRingIndex"], message: "Castle location requires a ring index." });
  if (location.kind !== "castle" && location.castleRingIndex !== null)
    context.addIssue({ code: "custom", path: ["castleRingIndex"], message: "Only Castle locations have ring indices." });
  if (location.kind === "castle" && !location.stableAssignmentRef)
    context.addIssue({ code: "custom", path: ["stableAssignmentRef"], message: "Castle location requires a stable assignment reference." });
}

export const neighbourhoodLocationV1Schema = neighbourhoodLocationBaseSchema.extend({
  allowedDefinitionIds: z.array(z.string().uuid()),
}).strict().superRefine(validateLocationShape);

export const neighbourhoodLocationV2Schema = neighbourhoodLocationBaseSchema
  .extend({
    allowedHuntDefinitionIds: z.array(z.string().uuid()),
    allowedResourceDefinitionIds: z.array(z.string().uuid()),
  })
  .strict()
  .superRefine(validateLocationShape);

export const neighbourhoodLocationSchema = z.union([
  neighbourhoodLocationV1Schema,
  neighbourhoodLocationV2Schema,
]);

export const neighbourhoodRouteSchema = z.object({
  id: z.string().uuid(),
  key: neighbourhoodKeySchema,
  name: z.string().min(1),
  originLocationId: z.string().uuid(),
  destinationLocationId: z.string().uuid(),
  bidirectional: z.boolean(),
  logicalDistance: z.number().finite().positive(),
  travelBandId: z.string().uuid().nullable(),
  durationSeconds: z.number().int().positive().nullable(),
  points: z.array(z.object({ x: coordinateSchema, y: coordinateSchema, sortOrder: z.number().int().nonnegative() }).strict()).min(2),
}).strict();

export const neighbourhoodTemplateV1Schema = z.object({
  contractVersion: z.literal(1),
  id: z.string().uuid(),
  key: neighbourhoodKeySchema,
  name: z.string().min(1),
  version: z.number().int().positive(),
  designWidth: z.number().int().positive(),
  designHeight: z.number().int().positive(),
  centreX: coordinateSchema,
  centreY: coordinateSchema,
  backgroundAssetId: z.string().uuid().nullable(),
  productionReady: z.boolean(),
  importedLegacyDraft: z.boolean(),
  archived: z.boolean(),
  sectors: z.array(neighbourhoodSectorSchema),
  travelBands: z.array(neighbourhoodTravelBandSchema),
  locations: z.array(neighbourhoodLocationV1Schema),
  routes: z.array(neighbourhoodRouteSchema),
}).strict();

export const neighbourhoodTemplateV2Schema = neighbourhoodTemplateV1Schema
  .omit({ contractVersion: true, locations: true })
  .extend({
    contractVersion: z.literal(2),
    locations: z.array(neighbourhoodLocationV2Schema),
  })
  .strict();

export const neighbourhoodTemplateV3Schema = neighbourhoodTemplateV2Schema
  .omit({ contractVersion: true, designWidth: true, designHeight: true })
  .extend({
    contractVersion: z.literal(3),
    coordinateSpaceVersion: z.literal(3),
    designWidth: z.literal(4096),
    designHeight: z.literal(4096),
    defaultCameraCenterX: z.number().finite().min(0).max(4096),
    defaultCameraCenterY: z.number().finite().min(0).max(4096),
    defaultCameraZoom: z.number().finite().min(0.05).max(8),
  })
  .strict();

export const neighbourhoodTemplateSchema = z.union([
  neighbourhoodTemplateV1Schema,
  neighbourhoodTemplateV2Schema,
  neighbourhoodTemplateV3Schema,
]);

export function upgradeNeighbourhoodTemplateV1(
  template: z.infer<typeof neighbourhoodTemplateV1Schema>,
): z.infer<typeof neighbourhoodTemplateV2Schema> {
  return {
    ...template,
    contractVersion: 2,
    locations: template.locations.map(({ allowedDefinitionIds, ...location }) => ({
      ...location,
      allowedHuntDefinitionIds: location.kind === "hunt" ? allowedDefinitionIds : [],
      allowedResourceDefinitionIds: [],
    })),
  };
}

/** Mirrors the one-time SQL affine conversion while preserving every stable row ID. */
export function upgradeNeighbourhoodTemplateV2(
  template: z.infer<typeof neighbourhoodTemplateV2Schema>,
): z.infer<typeof neighbourhoodTemplateV3Schema> {
  const scaleX = 4096 / template.designWidth;
  const scaleY = 4096 / template.designHeight;
  return {
    ...template,
    contractVersion: 3,
    coordinateSpaceVersion: 3,
    designWidth: 4096,
    designHeight: 4096,
    centreX: template.centreX * scaleX,
    centreY: template.centreY * scaleY,
    defaultCameraCenterX: template.centreX * scaleX,
    defaultCameraCenterY: template.centreY * scaleY,
    defaultCameraZoom: 1,
    productionReady: false,
    locations: template.locations.map((location) => ({
      ...location,
      x: location.x * scaleX,
      y: location.y * scaleY,
    })),
    sectors: template.sectors.map((sector) => ({
      ...sector,
      polygon: sector.polygon.map((point) => ({ x: point.x * scaleX, y: point.y * scaleY })),
    })),
    routes: template.routes.map((route) => ({
      ...route,
      points: route.points.map((point) => ({ ...point, x: point.x * scaleX, y: point.y * scaleY })),
    })),
  };
}

export type AnyNeighbourhoodTemplate = z.infer<typeof neighbourhoodTemplateSchema>;
export type NeighbourhoodTemplateV1 = z.infer<typeof neighbourhoodTemplateV1Schema>;
export type NeighbourhoodTemplateV2 = z.infer<typeof neighbourhoodTemplateV2Schema>;
export type NeighbourhoodTemplateV3 = z.infer<typeof neighbourhoodTemplateV3Schema>;
/** Phase 5 compatibility alias. New Phase 8 code should use NeighbourhoodTemplateV2 explicitly. */
export type NeighbourhoodTemplate = NeighbourhoodTemplateV1;
export type NeighbourhoodLocation = z.infer<typeof neighbourhoodLocationV1Schema>;
export type NeighbourhoodLocationV2 = z.infer<typeof neighbourhoodLocationV2Schema>;
export type NeighbourhoodRoute = z.infer<typeof neighbourhoodRouteSchema>;
export type NeighbourhoodSector = z.infer<typeof neighbourhoodSectorSchema>;
export type NeighbourhoodTravelBand = z.infer<typeof neighbourhoodTravelBandSchema>;

export type NeighbourhoodAssignmentReference = {
  templateId: string;
  castleLocationId: string;
  stableAssignmentRef: string;
  castleRingIndex: number;
};
